import { useEffect, useRef } from 'react';
import Cookies from 'js-cookie';
import FacebookPixel from 'react-facebook-pixel';
import {
  getOppref,
  initAmplitude,
  initGoogleTag,
  initLockerStudio,
  sendAmplitudeData,
} from '../../analytics';
import {
  COOKIE_UUID_KEY,
  ENV,
  EVENTS,
  FB_PIXEL_ID,
  GTM_ID,
  GTM_GTG_ID,
  HOTJAR_ID,
  IS_DEV,
  IS_PROD,
  OPENAI_PIXEL_ID,
} from '../constants';
import { hotjar, tiktok, oneLinkSmartScript, getGtmScript, gtg, openAIPixel } from '../scripts';
import { GOOGLE_FLOWS, type GoogleFlow } from '../utils/googleFlow';
import { useCookieYesDisabled } from './useCookieYesDisabled';
import { queryParser, useInlineScript } from '@applyft-web/utils';

const GTM_LOAD_TIMEOUT_MS = 10_000;

export const useInitAnalytics = (googleFlow: GoogleFlow) => {
  const isGtgFlow = googleFlow === GOOGLE_FLOWS.GTG;
  const { fbclid, ttclid } = queryParser(window.location.search);
  const inited = useRef(false);
  const amplitudeInited = useRef(false);
  const hotjarScript = HOTJAR_ID ? hotjar : '';
  const tiktokScript = ttclid ? tiktok : '';
  const openAIScript = OPENAI_PIXEL_ID && getOppref() ? openAIPixel : '';
  const isCookieYesDisabled = useCookieYesDisabled();
  const gtmScript = isGtgFlow ? '' : getGtmScript(GTM_ID, isCookieYesDisabled);
  const gtgScript = isGtgFlow && !IS_DEV ? gtg(GTM_GTG_ID) : '';
  const initCallback = (analytics: string) => {
    window.sessionStorage?.setItem(`${analytics}Init`, 'true');
  };
  const isGtmLoaded = useInlineScript(gtmScript, 'google_tag_manager');
  // `/gtg` landings load the gateway container instead of GTM, so they never wait for it
  const isWaitingForGtm = Boolean(gtmScript) && !isGtmLoaded;

  useEffect(() => {
    if (inited.current || isGtgFlow || isWaitingForGtm) return;
    initGoogleTag();
    inited.current = true;
  }, [isGtgFlow, isWaitingForGtm]);

  useEffect(() => {
    if (!isWaitingForGtm) return;

    const timeoutId = setTimeout(() => {
      if (!IS_PROD) {
        console.error(
          `GTM container ${GTM_ID} did not load within ${GTM_LOAD_TIMEOUT_MS}ms; Google Analytics and Google Ads are not initialized`,
        );
      }
      // TODO: move to Sentry once error monitoring is added
      sendAmplitudeData(EVENTS.GTM_LOAD_TIMEOUT, {
        gtm_id: GTM_ID,
        timeout_ms: GTM_LOAD_TIMEOUT_MS,
        page_path: window.location.pathname,
      });
    }, GTM_LOAD_TIMEOUT_MS);

    return () => clearTimeout(timeoutId);
  }, [isWaitingForGtm]);

  useEffect(() => {
    if (GTM_ID || IS_DEV) return;

    if (!IS_PROD) {
      console.error(
        'REACT_APP_GTM_ID is not set; the cookie banner is not loaded and Google Analytics and Google Ads start without consent defaults',
      );
    }
    // TODO: move to Sentry once error monitoring is added
    sendAmplitudeData(EVENTS.GTM_NOT_CONFIGURED, {
      env: ENV,
      page_path: window.location.pathname,
    });
  }, []);

  useEffect(() => {
    if (amplitudeInited.current) return;
    amplitudeInited.current = true;
    initAmplitude(undefined, initCallback);
  }, []);

  useEffect(() => {
    const checkCookies = setInterval(() => {
      const id = Cookies.get(COOKIE_UUID_KEY);
      if (id) {
        initLockerStudio(id, initCallback);

        if (fbclid && FB_PIXEL_ID) {
          // @ts-ignore
          FacebookPixel.init(FB_PIXEL_ID, { external_id: id });
          FacebookPixel.pageView();
        }

        clearInterval(checkCookies);
      }
    }, 500);

    const timeoutId = setTimeout(() => {
      clearInterval(checkCookies);
    }, 30_000);

    return () => {
      clearInterval(checkCookies);
      clearTimeout(timeoutId);
    };
  }, [fbclid]);

  useInlineScript(gtgScript, 'google_tag_gateway');

  useInlineScript(hotjarScript, 'hj');

  useInlineScript(tiktokScript, 'TiktokJelly');

  useInlineScript(openAIScript, 'oaiq');

  useInlineScript(oneLinkSmartScript, 'AF_SMART_SCRIPT');
};
